/**
 * Signaler un problème (conception/signaler.md, décision 146) — les règles
 * pures, partagées : le navigateur s'en sert pour prévenir, le serveur pour
 * refuser, sans croire le navigateur.
 *
 * Un signalement parle de la PLATEFORME, jamais d'un patient (R6, R7). Ces
 * règles sont une aide, pas une garantie : l'avertissement à l'écran reste la
 * règle de fond.
 */
/**
 * Un numéro malgache : `034 12 345 67`, `+261 34 12 345 67`, `0341234567`.
 * Les séparateurs usuels (espace, point, tiret) sont tolérés entre chiffres.
 */
const MOTIF_TELEPHONE = /(?:\+\s?261|\b0)[\s.-]?3[2-9](?:[\s.-]?\d){7}(?!\d)/;
/** Une année seule (`2026`) n'est pas un matricule : on écrit « depuis 2026 ». */
const ANNEE = /^(?:19|20)\d{2}$/;
/** Le numéro d'un autre signalement (`SIG-0042`) se cite sans être refusé. */
const NUMERO_SIGNALEMENT = /^SIG-\d+$/i;
/** Une date écrite avec des tirets (`21-10-2026`) non plus. */
const DATE_A_TIRETS = /^\d{1,2}-\d{1,2}-\d{2,4}$/;
/** Un mot : lettres, chiffres et tirets d'un seul tenant (`AFS-00123`). */
const MOT = /[\p{L}\p{N}-]+/gu;
/** À partir de quatre chiffres dans un même mot, on y voit un matricule. */
const CHIFFRES_D_UN_MATRICULE = 4;
function ressembleAUnMatricule(mot) {
    if (ANNEE.test(mot) || NUMERO_SIGNALEMENT.test(mot) || DATE_A_TIRETS.test(mot))
        return false;
    return (mot.match(/\d/g)?.length ?? 0) >= CHIFFRES_D_UN_MATRICULE;
}
/**
 * R6 — le texte semble-t-il porter une donnée de patient ?
 *
 * Un matricule du corpus est un mot chiffré (`004521`, `M0042`, `AFS-00123`,
 * en majuscules comme en minuscules) ; on refuse donc tout mot qui porte
 * quatre chiffres ou plus, hors année et numéro de signalement. Un code HTTP
 * (`500`), une heure (`10:45`) ou une version (`1.4.12`) passent.
 */
export function contientDonneeDePatient(texte) {
    if (MOTIF_TELEPHONE.test(texte))
        return true;
    return (texte.match(MOT) ?? []).some(ressembleAUnMatricule);
}
/**
 * R8 — un message d'erreur du navigateur, purgé avant d'être gardé : tout
 * mot qui ressemble à un matricule ou à un téléphone est remplacé, et le
 * message tronqué. Le message joint n'est jamais refusé : il n'est pas saisi.
 */
export const LONGUEUR_MESSAGE_ERREUR = 200;
export function epurerMessageErreur(message) {
    const sansTelephone = message.replace(new RegExp(MOTIF_TELEPHONE.source, 'g'), '…');
    const purge = sansTelephone.replace(MOT, (mot) => (ressembleAUnMatricule(mot) ? '…' : mot));
    return purge.slice(0, LONGUEUR_MESSAGE_ERREUR);
}
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/**
 * R7 — le chemin de l'écran, sans rien qui désigne une personne : sans
 * paramètre de requête ni fragment, et chaque segment qui porte un
 * identifiant (UUID, ou un chiffre) remplacé par `:id`.
 * `/personnes/3f2a…?q=dupont#x` → `/personnes/:id`.
 */
export function gabaritEcran(chemin) {
    const seul = chemin.split(/[?#]/)[0] ?? '';
    const segments = seul
        .split('/')
        .filter((s) => s !== '')
        .map((s) => (UUID.test(s) || /\d/.test(s) ? ':id' : s));
    return `/${segments.join('/')}`.slice(0, 200);
}
/** Le numéro lisible : `SIG-0042`. */
export function numeroSignalement(numero) {
    return `SIG-${String(numero).padStart(4, '0')}`;
}
//# sourceMappingURL=signalement.js.map